import {
  ServerCog,
  Plus,
  Loader2,
  Building2,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { CreateApplicationDialog } from '@/components/applications/CreateApplicationDialog.js';
import { api, type Application } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';

export function ApplicationsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { memberships } = useAuth();

  const query = useQuery({ queryKey: ['applications'], queryFn: () => api.applications.listAll() });

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ServerCog className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold tracking-tight">Applications</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Things you host. An application groups its environments, components, scripts, and
            backups under one organisation.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={memberships.length === 0}>
          <Plus className="h-4 w-4" />
          New application
        </Button>
      </header>

      {query.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Could not load applications.
          </CardContent>
        </Card>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} />
      ) : (
        <ApplicationsByOrg apps={query.data!} />
      )}

      <CreateApplicationDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={async () => {
          await queryClient.invalidateQueries({ queryKey: ['applications'] });
        }}
      />
    </div>
  );
}

function ApplicationsByOrg({ apps }: { apps: Application[] }): JSX.Element {
  // Group apps by org to make the tree-of-orgs structure visible.
  const byOrg = new Map<string, { orgName: string; orgSlug: string; apps: Application[] }>();
  for (const app of apps) {
    if (!byOrg.has(app.orgId)) {
      byOrg.set(app.orgId, { orgName: app.orgName, orgSlug: app.orgSlug, apps: [] });
    }
    byOrg.get(app.orgId)!.apps.push(app);
  }
  return (
    <div className="space-y-6">
      {[...byOrg.entries()].map(([orgId, group]) => (
        <section key={orgId} className="space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" />
            <Link to="/orgs/$slug" params={{ slug: group.orgSlug }} className="hover:text-foreground">
              {group.orgName}
            </Link>
            <span className="font-mono normal-case">/{group.orgSlug}</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {group.apps.map((app) => (
              <ApplicationCard key={app.id} app={app} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ApplicationCard({ app }: { app: Application }): JSX.Element {
  // CardHeader ships only `pt-5` — bottom padding lives on CardContent.
  // When there's no description, CardContent isn't rendered and the slug
  // sits flush against the bottom edge of the card. Add pb-5 explicitly in
  // that branch so the card always has visual breathing room.
  const hasDescription = Boolean(app.description);
  return (
    <Link
      to="/p/applications/applications/$slug"
      params={{ slug: app.slug }}
      search={{ org: app.orgSlug }}
      className="block"
    >
      <Card className="h-full transition-colors hover:border-primary/60">
        <CardHeader className={hasDescription ? 'space-y-1' : 'space-y-1 pb-5'}>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base">{app.name}</CardTitle>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <CardDescription className="text-xs">
            <span className="font-mono">/{app.slug}</span>
            {app.templateId && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                <Sparkles className="h-2.5 w-2.5" />
                {app.templateId}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        {hasDescription && (
          <CardContent className="pt-0">
            <p className="line-clamp-2 text-sm text-muted-foreground">{app.description}</p>
          </CardContent>
        )}
      </Card>
    </Link>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <ServerCog className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-medium">No applications yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Create your first application to start managing its environments, components, and scripts.
        </p>
        <Button onClick={onCreate} className="mt-4">
          <Plus className="h-4 w-4" />
          New application
        </Button>
      </CardContent>
    </Card>
  );
}
